import { Form as InertiaForm } from '@inertiajs/react'
import { type ComponentProps } from 'react'

import { CollectSessionType, DeviceOptionType, UserOptionType, AreaOptionType } from './types'

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  collect_session: CollectSessionType
  submitText: string
  devices?: DeviceOptionType[]
  users?: UserOptionType[]
  areas?: AreaOptionType[]
}

export default function Form({
  collect_session,
  submitText,
  devices = [],
  users = [],
  areas = [],
  ...formProps
}: FormProps) {
  return (
    <InertiaForm
      transform={data => ({ collect_session: data })}
      className="contents"
      {...formProps}
    >
      {({ errors, processing }) => (
        <>
          <div className="my-5">
            <label htmlFor="device_id">Device</label>
            <select
              name="device_id"
              id="device_id"
              defaultValue={collect_session.device_id || devices[0]?.id || ''}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            >
              <option value="" disabled>
                Select device
              </option>
              {devices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name} {device.code ? `(${device.code})` : ''}
                </option>
              ))}
            </select>
            {errors.device_id && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.device_id.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="user_id">User</label>
            <select
              name="user_id"
              id="user_id"
              defaultValue={collect_session.user_id || users[0]?.id || ''}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            >
              <option value="" disabled>
                Select user
              </option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} {user.email ? `(${user.email})` : ''}
                </option>
              ))}
            </select>
            {errors.user_id && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.user_id.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="area_ids">Areas</label>
            <select
              name="area_ids[]"
              id="area_ids"
              multiple
              defaultValue={collect_session.area_ids || []}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            >
              {areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name} {area.code ? `(${area.code})` : ''}
                </option>
              ))}
            </select>
            {errors.area_ids && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.area_ids.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="start_at">Start at</label>
            <input
              type="datetime-local"
              name="start_at"
              id="start_at"
              defaultValue={collect_session.start_at}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.start_at && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.start_at.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="end_at">End at</label>
            <input
              type="datetime-local"
              name="end_at"
              id="end_at"
              defaultValue={collect_session.end_at}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.end_at && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.end_at.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="physical_amount_collected">Physical amount collected</label>
            <input
              type="number"
              name="physical_amount_collected"
              id="physical_amount_collected"
              defaultValue={collect_session.physical_amount_collected}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.physical_amount_collected && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.physical_amount_collected.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="status">Status</label>
            <input
              type="text"
              name="status"
              id="status"
              defaultValue={collect_session.status}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.status && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.status.join(', ')}
              </div>
            )}
          </div>

          <div className="inline">
            <button
              type="submit"
              disabled={processing}
              className="rounded-lg py-3 px-5 bg-blue-600 text-white inline-block font-medium cursor-pointer"
            >
              {submitText}
            </button>
          </div>
        </>
      )}
    </InertiaForm>
  )
}
